import { useEffect, useState, useMemo } from "react";
import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Zap, Loader2, Flag, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import ActionBlockRenderer from "../components/ActionBlockRenderer";
import PublicPageOverlay from "../components/PublicPageOverlay";
import HubEngagementBar from "../components/HubEngagementBar";
import SeoHead from "../components/SeoHead";
import PwaInstallChip from "../components/PwaInstallChip";
import { api, getSessionToken as getToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ClonePreviewModal from "../components/ClonePreviewModal";
import BioPushOptInSheet from "../components/BioPushOptInSheet";
import { deriveLinksVars } from "../lib/contrastCheck";
import { CreatorHeader } from "../components/publicHub/CreatorHeader";
import { PageGrid } from "../components/publicHub/PageGrid";
import { ProductGrid } from "../components/publicHub/ProductGrid";
import { CouponGrid } from "../components/publicHub/CouponGrid";
import { DemoRibbon } from "../components/publicHub/DemoRibbon";
import { orderAdsLast, rememberQrVisit } from "../lib/quickPromote";
import "../styles/action-hub-themes.css";
import { bootFetch } from "../lib/bootFetch";

/**
 * Creator Action Hub — public hub at /u/:creator_slug.
 *
 * This is the creator's front door. Fans + brands can:
 *   * Browse all published Action Pages (pinned pages first)
 *   * See featured products with Buy links
 *   * Grab active coupon codes (click-to-copy + redemption tracking)
 *
 * Fires a visit event on mount + click events on every card so the author
 * can see hub-specific analytics at /app/action-hub/analytics (Hub tab).
 */

function getOrMintVisitorId() {
  try {
    const KEY = "creatoraiq.ah_vid";
    let v = localStorage.getItem(KEY);
    if (!v) {
      v = "vst_" + Math.random().toString(36).slice(2, 14) + Date.now().toString(36).slice(-4);
      localStorage.setItem(KEY, v);
    }
    return v;
  } catch {
    return "vst_ephemeral";
  }
}

export default function PublicCreatorDirectory() {
  const { creator_slug: slug } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [copiedCoupon, setCopiedCoupon] = useState(null);
  const [gateUnlocked, setGateUnlocked] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [showClonePreview, setShowClonePreview] = useState(false);
  // v148.x-hotfix — Inline sign-in modal state.
  const [signinOpen, setSigninOpen] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const visitorId = useMemo(() => getOrMintVisitorId(), []);
  // v149.2 — Smart QR scans land with ?qr=CODE; remember it for ad attribution.
  useEffect(() => { rememberQrVisit(slug); }, [slug]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // v149.64 — Often already on its way: index.html started it.
        const res = await bootFetch(`${backend}/api/public/creators/${encodeURIComponent(slug)}`);
        if (!res.ok) {
          if (!cancelled) setState({ loading: false, data: null, error: res.status === 404 ? "Creator not found" : `Error ${res.status}` });
          return;
        }
        const json = await res.json();
        if (!cancelled) setState({ loading: false, data: json, error: null });

        // Fire hub visit tracking (fire-and-forget)
        fetch(`${backend}/api/public/creators/${encodeURIComponent(slug)}/visit`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            visitor_id: visitorId,
            referrer: document.referrer || null,
            ua: navigator.userAgent,
          }),
        }).catch(() => {});
      } catch {
        if (!cancelled) setState({ loading: false, data: null, error: "Network error" });
      }
    })();
    return () => { cancelled = true; };
  }, [slug, backend, visitorId]);

  // v89 (Feb 2026) — page_gate detection & unlock persistence for the Bio Hub.
  // v149 — The Quick Promote ads block always renders last.
  const hubBlocksRaw = orderAdsLast(state.data?.hub?.blocks || []);
  const gateIdx = hubBlocksRaw.findIndex((b) => b?.type === "page_gate");
  const gateBlock = gateIdx >= 0 ? hubBlocksRaw[gateIdx] : null;
  const gateRemember = gateBlock ? (gateBlock.config?.remember !== false) : false;
  const gateKey = gateBlock
    ? `ah:gate:hub:${gateBlock.block_id}`
    : null;

  useEffect(() => {
    if (!gateKey || !gateRemember) return;
    try {
      if (localStorage.getItem(gateKey)) setGateUnlocked(true);
    } catch { /* localStorage disabled */ }
  }, [gateKey, gateRemember]);

  // v138.3 — Auto-open the clone-preview modal when a signed-in user
  // arrives here from the landing-page auto-clone flow. v147.34 —
  // Also fires for non-demo creator pages (Copy this design flow).
  const creatorIsDemo = state.data?.creator?.is_demo;
  useEffect(() => {
    if (state.loading || !state.data?.creator) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("open_clone_preview") === "1" && getToken()) {
      setShowClonePreview(true);
      // Clean up the URL so a page refresh doesn't re-trigger.
      const url = new URL(window.location.href);
      url.searchParams.delete("open_clone_preview");
      window.history.replaceState({}, "", url.toString());
    }
  }, [state.loading, state.data?.creator]);

  // v142 — Loyalty: capture `?ref=<account_id>` and stamp the referral
  // once per page load. TTL + idempotency live server-side, so calling
  // this on every mount is safe.
  useEffect(() => {
    if (state.loading || !slug) return;
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (!ref) return;
    fetch(`${backend}/api/public/creators/${encodeURIComponent(slug)}/loyalty/ref`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ visitor_id: visitorId, referrer_account_id: ref }),
    }).catch(() => {});
  }, [state.loading, slug, visitorId]);


  const handleGateUnlock = () => {
    setGateUnlocked(true);
    if (gateKey && gateRemember) {
      try { localStorage.setItem(gateKey, new Date().toISOString()); } catch { /* noop */ }
    }
  };

  const trackClick = (target_type, target_id, href, block_id) => {
    // fire-and-forget
    fetch(`${backend}/api/public/creators/${encodeURIComponent(slug)}/click`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        visitor_id: visitorId,
        target_type,
        target_id,
        href: href || null,
        block_id: block_id || null,
      }),
    }).catch(() => {});
  };

  const redeemCoupon = async (coupon) => {
    trackClick("coupon", coupon.coupon_id);
    try {
      await navigator.clipboard.writeText(coupon.code);
      setCopiedCoupon(coupon.coupon_id);
      toast.success(`Copied "${coupon.code}"`);
      setTimeout(() => setCopiedCoupon(null), 1800);
      fetch(`${backend}/api/public/coupons/${coupon.coupon_id}/redeem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visitor_id: visitorId }),
      }).catch(() => {});
    } catch {
      toast.error("Couldn't copy — copy it manually");
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center p-6">
        <p className="overline"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</p>
      </div>
    );
  }

  if (state.error || !state.data) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center p-6">
        <div
          className="rounded-2xl bg-white p-6 max-w-md w-full text-center"
          style={{ border: "1px solid var(--ux-border)", boxShadow: "var(--ux-shadow-1)" }}
          data-testid="creator-directory-error"
        >
          <p className="overline mb-2">Not found</p>
          <p className="text-sm" style={{ color: "var(--ux-ink-2)" }}>
            We couldn&apos;t find the creator <code className="font-mono">/u/{slug}</code>.
          </p>
          <Link to="/" className="inline-flex items-center mt-4 ux-btn ux-btn-primary">
            Go home
          </Link>
        </div>
      </div>
    );
  }

  const { creator, pages, coupons, products, counts, hub, settings } = state.data;
  // v147.98 — Public bio hub `affiliate_picks` block was rendering
  // empty because the backend hydrated `affiliate_products` at
  // `/public/creators/{slug}` but this loader never destructured it
  // into `hubContext`. Now surfaced so picks the creator makes in
  // Affiliate Marketplace actually show on their bio.
  const affiliateProducts = Array.isArray(state.data?.affiliate_products)
    ? state.data.affiliate_products : [];
  const hasAnything = counts.pages > 0 || counts.coupons > 0 || counts.products > 0;
  const igHandle = (creator.instagram_handle || "").replace(/^@/, "");
  const hubBlocks = hub?.blocks || [];
  const useCustomHub = hubBlocks.length > 0;
  const hubTheme = hub?.theme || "classic";
  // v146.35 — Per-theme customisation (Linktree-style palette). When
  // set, the CSS vars below win over the defaults declared in
  // action-hub-themes.css for the .ah-links block.
  const linksSettings = (hub?.theme_settings && hub.theme_settings.links) || {};
  // v146.53 — auto-derive muted + block-fg from the creator's palette
  // so sub-text and pill-text never render invisible.
  const linksStyle = hubTheme === "links" ? (() => {
    const bg = linksSettings.bg || "#4a5568";
    const pill = linksSettings.pill || "#ffffff";
    const fg = linksSettings.fg || "#1a1a1a";
    const derived = deriveLinksVars({ bg, pill, fg });
    return {
      "--ah-links-bg": bg,
      "--ah-links-pill": pill,
      "--ah-links-fg": fg,
      "--ah-links-block-fg": derived.blockFg,
      "--ah-links-muted": derived.muted,
      "--ah-links-block-muted": derived.blockMuted,
      "--ah-links-bg-image": linksSettings.bg_image ? `url("${linksSettings.bg_image}")` : "none",
    };
  })() : undefined;

  // v145.5 — When this page is loaded inside an iframe on the marketing
  // Landing (Live Preview section), the URL carries `?embed=1` or
  // `?hide-clone=1`. In that case we suppress the sticky "Use as starting
  // point" ribbon so it doesn't overflow the phone mockup. Any other
  // interaction still works normally.
  // v147.47 — Split the two things `embed=1` used to gate:
  //   * `isEmbeddedPreview` — layout tweaks (drop the 5rem top pad
  //     used for clearing the floating "Join Linkola/Share" pills,
  //     hide the sticky demo ribbon). Fires for both Landing iframes
  //     AND the in-app `/app/view/u/*` route.
  //   * `hideClonePrompt` — suppress the footer "Copy this design"
  //     link. Only fires when the caller EXPLICITLY passes
  //     `hide-clone=1` (Landing demo previews); the in-app view
  //     surface still wants the link so trending visitors can grab
  //     the design.
  const isEmbeddedPreview = (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return p.get("embed") === "1" || p.get("hide-clone") === "1";
    } catch {
      return false;
    }
  })();
  const hideClonePrompt = (() => {
    try {
      return new URLSearchParams(window.location.search).get("hide-clone") === "1";
    } catch {
      return false;
    }
  })();

  // v146.10 — ViewInApp (the in-app "Bio" tab preview at /app/view/u/:slug)
  // wraps this page in an iframe that already sits under AppShell's own
  // header/back-button chrome. PublicPageOverlay's floating back/name/share
  // pills are redundant there (and on native, its back icon now drives its
  // own history stack — see PublicPageOverlay's v146.9 comment — producing
  // a confusing second back button). ViewInApp passes `hide-overlay=1` to
  // opt into hiding it; the Landing page's live-preview iframe does not
  // pass this flag, so that preview (no surrounding app chrome) is unaffected.
  const hideOverlay = (() => {
    try {
      return new URLSearchParams(window.location.search).get("hide-overlay") === "1";
    } catch {
      return false;
    }
  })();

  // v138.2 — "Use this as my starting point" flow for demo hubs.
  // v147.34 — Extended to work on ANY creator's hub (non-demo).
  // Two-step: (1) opens a preview modal showing exactly what will be
  // cloned + a replace/append choice; (2) commits the clone.
  //   Logged in → open preview → confirm → POST clone endpoint → hub.
  //   Logged out → send to signup with a `?clone_after_signin=…` param;
  //     landing's useEffect bounces them back here and auto-opens
  //     the preview modal once auth resolves.
  const openClonePreview = () => {
    // v147.34 — Non-demo public creators have `user_id: null` for privacy,
    // so `slug` is the only reliable identifier here. Both endpoints
    // accept slug lookup as a fallback.
    if (!creator?.slug) return;
    if (!getToken()) {
      try {
        sessionStorage.setItem("pending_demo_clone", JSON.stringify({
          source_user_id: creator.user_id || null,
          source_slug: creator.slug,
          source_name: creator.name,
          is_demo: !!creator.is_demo,
        }));
      } catch { /* sessionStorage disabled */ }
      toast.info("Sign up in 30s — we'll bring you back to copy this design");
      navigate(`/signup?clone_after_signin=${encodeURIComponent(creator.slug)}`);
      return;
    }
    setShowClonePreview(true);
  };

  const commitClone = async (replace) => {
    if (cloning) return;
    setCloning(true);
    try {
      const isDemo = !!creator?.is_demo;
      const endpoint = isDemo
        ? "/action-hub/hub/clone-from-demo"
        : "/action-hub/hub/clone-from-creator";
      // Demo payload historically expects source_user_id; the new
      // clone-from-creator endpoint accepts either source_user_id OR
      // source_slug and resolves the target internally.
      const body = isDemo
        ? { source_user_id: creator.user_id, replace }
        : { source_slug: creator.slug, replace };
      const { data } = await api.post(endpoint, body);
      toast.success(`Copied ${data.blocks_created || 0} blocks — welcome home!`);
      setShowClonePreview(false);
      navigate("/app/action-hub/hub");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't copy this design. Please try again.";
      toast.error(msg);
    } finally {
      setCloning(false);
    }
  };

  // v140 — Universal block-click tracker. Every CTA rendered inside
  // ActionBlockRenderer (link, social, whatsapp, app_download, video,
  // FAQ, book, review, phone, email, download, follow, like, message,
  // etc.) funnels through this handler so the dashboard "Clicks" tile
  // + Full Analytics per-block chart reflect reality — not just the
  // 4 legacy card types (page / product / coupon / ig).
  const mapBlockTypeToTargetType = (t) => {
    const T = (t || "").toLowerCase();
    // Direct pass-throughs — types already whitelisted server-side.
    const known = new Set([
      "social", "app_download", "whatsapp", "phone", "email",
      "faq", "video", "embed", "link", "book", "appointment",
      "ticket", "registration", "subscription", "location",
      "brand_collab", "event", "meetup", "download", "storefront",
      "featured_product", "lead", "poll", "follow", "like", "message",
      "review", "google_review",
    ]);
    if (known.has(T)) return T;
    // Fallbacks for legacy block types
    if (T === "socials") return "social";
    if (T === "hero" || T === "header" || T === "text") return "link";
    if (T === "image" || T === "avatar" || T === "grid") return "link";
    return "external";
  };
  const trackBlockClick = (block, href) => {
    if (!block) return;
    const ttype = mapBlockTypeToTargetType(block.type);
    // Prefer block_id as target_id so the analytics roll up per-block.
    // Use block_id both as target_id and as block_id so per-block filter
    // works in both grouping dimensions.
    trackClick(ttype, block.block_id || `blk_${Date.now()}`, href, block.block_id);
  };

  // Handlers passed into ActionBlockRenderer for hub-only blocks
  const hubContext = {
    creator,
    slug: creator.slug,
    pages,
    products,
    coupons,
    // v147.98 — Feed the creator's active affiliate picks through so
    // the `affiliate_picks` block on the bio hub actually renders
    // them instead of falling back to "No affiliate picks yet."
    affiliate_products: affiliateProducts,
    // v113 — plumb storefront settings so the embedded `storefront` block
    // renders the same OTP-gated buy modal as /store/{slug}.
    settings: settings || {},
    pageHref: (p) => `/u/${creator.slug}/${p.slug}`,
    onPageClick: (_e, p) => trackClick("page", p.page_id, `/u/${creator.slug}/${p.slug}`),
    onProductClick: (_e, p) => trackClick("product", p.product_id, p.buy_url),
    onCouponClick: (_e, cp) => redeemCoupon(cp),
  };

  return (
    <div
      className={`min-h-screen ah-theme ah-${hubTheme} ah-root`}
      data-testid="creator-directory"
      style={linksStyle}
    >
      {/* v146.33 — Dynamic SEO tags per-creator (title, meta description,
          canonical, OG, Twitter, JSON-LD Person/LocalBusiness). Fed from
          /api/seo/meta/creator/{slug} so Googlebot's JS-render pass and
          AI crawlers pick up creator-specific metadata instead of the
          default Linkola shell. */}
      <SeoHead
        endpoint={slug ? `/seo/meta/creator/${encodeURIComponent(slug)}` : null}
        fallback={{
          title: state.data?.user?.name ? `${state.data.user.name} · Linkola` : "Linkola",
          description: state.data?.user?.bio || undefined,
          image: state.data?.user?.picture || undefined,
        }}
      />
      {/* v147.47 — When embedded (inside AppShell's `/app/view/u/*`
          route OR a Landing iframe preview) drop the 5rem top pad
          that was reserved for the floating "Join Linkola / Share"
          pills. Both surrounding surfaces already provide their own
          top chrome + safe-area, so re-adding another 5rem here
          double-pads the top on iOS (small notch) → the top row of
          engagement buttons was getting hidden behind AppShell's
          fixed header on native.  Standalone `/u/{slug}` (no embed,
          no in-app wrapper) keeps the full 5rem clearance. */}
      <div className={`max-w-3xl mx-auto px-4 sm:px-8 pb-4 sm:pb-8 ${
        isEmbeddedPreview
          ? "pt-3 sm:pt-4"
          : "pt-[calc(5rem+env(safe-area-inset-top,0))] sm:pt-24"
      }`}>
        {/* Engagement bar — sits above every layout so likes / follow /
            private comments are always visible without design changes. */}
        <div className="mb-4 flex justify-center">
          <HubEngagementBar creatorSlug={slug} />
        </div>
        {useCustomHub ? (
          /* Block-based hub — creator-designed layout */
          <div className="space-y-4" data-testid="creator-directory-blocks">
            {(gateBlock && !gateUnlocked
              ? hubBlocksRaw.slice(0, gateIdx + 1)
              : hubBlocksRaw
            ).map((b) => (
              <div key={b.block_id} data-block-anchor={b.block_id}>
                <ActionBlockRenderer
                  block={b}
                  hubContext={hubContext}
                  onClick={trackBlockClick}
                  onGateUnlock={handleGateUnlock}
                />
              </div>
            ))}
            {gateBlock && !gateUnlocked && hubBlocksRaw.length > gateIdx + 1 && (
              <p className="text-center text-xs ah-muted italic pt-1"
                 data-testid="creator-directory-gate-hint">
                🔒 {hubBlocksRaw.length - gateIdx - 1} more block{hubBlocksRaw.length - gateIdx - 1 === 1 ? "" : "s"} unlock after you complete the step above.
              </p>
            )}
            {/* v145.18 — Removed the auto-appended italic `hub.bio` footer.
                Creators found it confusing ("Where is 'Best clothing'
                coming from? It isn't a block in my designer.") — the bio
                is already shown in the hero header block, so surfacing
                it again at the bottom just duplicated the info without
                appearing anywhere in the design editor. */}
          </div>
        ) : (
          /* Legacy fixed layout — kept as fallback for creators who haven't
             customized their hub yet. */
        <>
        <CreatorHeader
          creator={creator}
          counts={counts}
          hasAnything={hasAnything}
          igHandle={igHandle}
          trackClick={trackClick}
        />

        {!hasAnything ? (
          <div
            className="rounded-2xl bg-white p-8 text-center"
            style={{ border: "1px dashed var(--ux-border-strong)" }}
            data-testid="creator-directory-empty"
          >
            <p className="text-sm" style={{ color: "var(--ux-ink-2)" }}>
              {creator.name || "This creator"} hasn&apos;t published anything yet.
            </p>
            <p className="text-xs mt-2" style={{ color: "var(--ux-ink-3)" }}>Check back soon!</p>
          </div>
        ) : (
          <div className="space-y-8">
            <PageGrid pages={pages} creatorSlug={creator.slug} trackClick={trackClick} />
            <ProductGrid products={products} trackClick={trackClick} />
            <CouponGrid coupons={coupons} copiedCoupon={copiedCoupon} onRedeem={redeemCoupon} />
          </div>
        )}
        </>
        )}

        <footer className="text-center mt-12 space-y-3">
          {/* v148.x-hotfix — Inline sign-in CTA for anonymous visitors.
              Opens an in-page modal (AuthTabs) so the visitor lands
              back on this bio with their coin chip live. Hidden when
              already signed in. */}
          {!authUser && (
            <button
              type="button"
              onClick={() => setSigninOpen(true)}
              data-testid="hub-inline-signin-btn"
              className="text-[13px] font-black underline text-ink"
            >
              Sign in to see your coin balance & rewards →
            </button>
          )}
          <p className="text-xs text-ink/50">
            <Zap className="w-3 h-3 inline mr-1" />
            Powered by <b>Linkola</b>
          </p>
          {/* v147.39 — Downgraded "Copy this design" from a sticky
              bottom ribbon to a soft inline footer link so it doesn't
              dominate the page for logged-in visitors. Same handler:
              triggers preview modal for signed-in visitors, sends
              logged-out visitors through signup → back here → auto-open
              modal. Hidden on own hub, demo hubs (DemoRibbon already
              shows), and inside the in-app iframe preview. */}
          {creator && !creator?.is_demo && !hideClonePrompt
            && creator?.slug !== authUser?.creator_slug && (
            <p className="text-[11px] text-ink/60">
              <button
                type="button"
                onClick={openClonePreview}
                disabled={cloning}
                className="underline underline-offset-2 hover:text-coral font-semibold disabled:opacity-60"
                data-testid="copy-design-footer-link"
              >
                {cloning
                  ? "Copying…"
                  : (authUser
                      ? "Copy this design to your Web Link"
                      : "Get your own free Web Link like this →")}
              </button>
            </p>
          )}
          {/* v143.9 (Feb 2026) — Report link required by Google Play Child
              Safety Standards. Uses a mailto: handler with a pre-filled
              subject that includes the reported profile's slug so the
              trust-and-safety team can triage without asking the reporter
              for follow-up details. */}
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-ink/50">
            <a
              href={`mailto:abuse@linkola.app?subject=${encodeURIComponent(
                `Report profile @${slug}`,
              )}&body=${encodeURIComponent(
                `I'd like to report the profile at https://linkola.app/u/${slug}\n\nReason (please describe):\n\n`,
              )}`}
              className="inline-flex items-center gap-1 font-bold underline underline-offset-2 hover:text-coral"
              data-testid="public-creator-report-btn"
            >
              <Flag className="w-3 h-3" strokeWidth={2.5} /> Report this profile
            </a>
            <Link
              to="/child-safety-standards"
              className="inline-flex items-center gap-1 font-bold underline underline-offset-2 hover:text-coral"
              data-testid="public-creator-child-safety-link"
            >
              <ShieldAlert className="w-3 h-3" strokeWidth={2.5} /> Child Safety
            </Link>
          </div>
        </footer>
      </div>

      {/* Floating overlay: Join Linkola (left) + Share (right) — same
          brand mark + share sheet used on individual action pages.
          Hidden inside the in-app ViewInApp preview (`hide-overlay=1`) —
          AppShell already provides back/share affordances there. */}
      {!hideOverlay && (
        <PublicPageOverlay
          creatorSlug={slug}
          pageTitle={creator?.name || `@${slug}`}
        />
      )}

      {/* v146.38 — Per-bio PWA install chip (bottom-right). Injects a
          dynamic web manifest so visitors can save the creator's hub
          as a chromeless home-screen app. Hidden inside the native
          Capacitor app, in standalone mode, or when the visitor
          arrived from an already-installed home-screen tap. */}
      {!isEmbeddedPreview && !hideOverlay && creator && (
        <PwaInstallChip slug={slug} creatorName={creator?.name || `@${slug}`} />
      )}

      {/* v138.2 — Demo-hub CTA. Sticky ribbon at the bottom of demo pages
          only. Clones this hub's theme + blocks into the visitor's own
          hub (with a signup detour for logged-out users).
          v145.5 — Hidden when the page is loaded in an iframe with
          `?hide-clone=1` or `?embed=1` (used by the Landing live
          preview) so the ribbon doesn't clutter the phone mockup. */}
      {creator?.is_demo && !isEmbeddedPreview && (
        <DemoRibbon
          hubTheme={hubTheme}
          cloning={cloning}
          onOpenClonePreview={openClonePreview}
        />
      )}

      {/* v147.39 — Sticky "Copy this design" ribbon removed. Replaced
          by a soft underlined link inside the footer (see above) so
          signed-in visitors aren't nagged with a full-width CTA. */}

      {/* v138.3 — Clone-preview modal. Shows exactly what the visitor
          will get before we touch their existing hub. Replace vs. Add
          toggle lets first-time creators wipe cleanly, or lets existing
          creators splice the demo on top of their current blocks.
          v147.34 — Renders for any creator (not just demos). */}
      {showClonePreview && creator && (
        <ClonePreviewModal
          creator={creator}
          hubTheme={hubTheme}
          hubBlocks={hubBlocks}
          userPlanId={authUser?.action_hub_plan_id || "launch"}
          cloning={cloning}
          onClose={() => !cloning && setShowClonePreview(false)}
          onConfirm={commitClone}
        />
      )}

      {/* v147.33 — Bio Page push-notification opt-in prompt. Auto-opens
          ~5s after load if the brand has the channel enabled and the
          visitor hasn't already subscribed / snoozed. Silent no-op on
          brands that haven't turned Bio Push on. */}
      {slug && !creator?.is_demo && (
        <BioPushOptInSheet
          slug={slug}
          brandName={creator?.name}
          brandPicture={creator?.picture}
        />
      )}

      {/* v148.x-hotfix — Inline sign-in modal. Reuses AuthTabs so the
          visitor doesn't lose the bio page. AuthContext refreshes the
          `authUser` state on success, which cascades to the
          HubEngagementBar chip. */}
      {signinOpen && (
        <InlineSigninModal onClose={() => setSigninOpen(false)} />
      )}
    </div>
  );
}


// v148.x-hotfix — Lightweight sign-in bottom sheet on the public bio
// page. AuthTabs handles the actual sign-in / sign-up flow; on
// success we close the modal and rely on AuthContext to trigger a
// re-render of the parent so the balance chip appears.
function InlineSigninModal({ onClose }) {
  const AuthTabs = React.lazy(() => import("../components/AuthTabs"));
  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="hub-inline-signin-modal"
    >
      <div
        className="bg-white border-4 border-ink shadow-brutal w-full sm:max-w-sm flex flex-col rounded-t-lg sm:rounded-none max-h-[92dvh]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 p-4 border-b-2 border-ink shrink-0">
          <h3 className="font-black text-lg">Sign in to Linkola</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="hub-inline-signin-close"
            className="border-2 border-ink bg-white p-1 hover:bg-coral hover:text-white"
          >
            ✕
          </button>
        </div>
        <div className="p-4 overflow-y-auto min-h-0" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}>
          <React.Suspense fallback={<p className="text-xs text-ink/60">Loading…</p>}>
            <AuthTabs defaultTab="phone" initialMode="login" compact onSuccess={onClose} />
          </React.Suspense>
        </div>
      </div>
    </div>
  );
}


